import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * Read-only operational detail for a property the owner is assigned to.
 * Backed by the `owner_property_detail` SECURITY DEFINER RPC, which verifies
 * ownership server-side and returns NULL for properties the caller doesn't own.
 */

export type OwnerRoom = {
  id: number;
  title: string | null;
  floor: string | null;
  size_sqm: number | null;
  status: string | null;
  available: boolean | null;
  monthly_rent: number | null;
  occupied: boolean;
  tenant_name: string | null;
};

export type OwnerTenant = {
  booking_id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  /** Pairs a tenant with their room by key. The overview used to match on
   *  room_title, which is not unique across a building's apartments. */
  room_id: number | null;
  room_title: string | null;
  move_in_date: string | null;
  move_out_date: string | null;
  status: string | null;
  contract_status: string | null;
  signed_contract_url: string | null;
  /** Stored dunning status. Kept for compatibility; the UI derives the badge
   *  from the raw fields below instead (see isPaidCurrentPeriod). */
  payment_status: string | null;
  monthly_rent: number | null;
  deposit_amount: number | null;
  tenant_active: boolean | null;
  // Derived-overdue inputs — mirror the admin/owner reminder surfaces.
  rent_due_date: number | null;
  last_payment_received_at: string | null;
  rent_paid_through: string | null;
  manually_marked_paid: boolean | null;
  /** Suppression resolved server-side: apartment override ?? property ?? true. */
  reminders_enabled: boolean | null;
};

/**
 * A tenancy that has ended — the History tab. Not the same thing as the old
 * `moveouts` list, which was UPCOMING move-outs of people still living there.
 * Carries no status: the row is in this list precisely because the tenancy is
 * over, so a status column could only repeat that.
 */
export type OwnerHistoryEntry = {
  booking_id: number;
  name: string | null;
  room_id: number | null;
  room_title: string | null;
  /** When the tenancy actually ended: actual ?? planned ?? the booking's end. */
  move_out_date: string | null;
};

export type OwnerPropertyDetail = {
  property: {
    id: number;
    title: string | null;
    city: string | null;
    address: string | null;
    /**
     * Optional: `owner_property_detail` does not select it yet, so the header's
     * address line renders street · city until the RPC adds it. Declared here
     * because the page already asks for it.
     */
    zip_code?: string | null;
  } | null;
  rooms: OwnerRoom[];
  /** Everyone living there now, including tenants who have given notice. */
  tenants: OwnerTenant[];
  /** Everyone who has left. Complementary to `tenants` — see the RPC. */
  history: OwnerHistoryEntry[];
};

export function useOwnerPropertyDetail(propertyId: number | null) {
  return useQuery({
    queryKey: ["owner-property-detail", propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<OwnerPropertyDetail | null> => {
      const { data, error } = await supabase.rpc("owner_property_detail", {
        _property_id: propertyId,
      });
      if (error) throw error;
      return data as OwnerPropertyDetail | null;
    },
  });
}

/**
 * One room, read-only — what the property overview's room link opens. Before
 * this existed, clicking a room opened the TENANT dialog, which is the bug
 * this view was built to fix.
 */
export type OwnerRoomDetail = {
  room: {
    id: number;
    title: string | null;
    floor: string | null;
    size_sqm: number | null;
    status: string | null;
    available: boolean | null;
    monthly_rent: number | null;
    apartment_id: number | null;
    apartment_label: string | null;
    property_id: number;
    property_title: string | null;
  } | null;
  /**
   * Null when the room is empty. Carries the same raw fields
   * OwnerTenantDetailDialog derives the payment badge from (isPaidCurrentPeriod)
   * as the property overview's `tenants` rows do — see 20260815100000.
   */
  current_tenant: {
    booking_id: number;
    name: string | null;
    email: string | null;
    phone: string | null;
    move_in_date: string | null;
    move_out_date: string | null;
    monthly_rent: number | null;
    contract_status: string | null;
    signed_contract_url: string | null;
    payment_status: string | null;
    deposit_amount: number | null;
    tenant_active: boolean | null;
    rent_due_date: number | null;
    last_payment_received_at: string | null;
    rent_paid_through: string | null;
    manually_marked_paid: boolean | null;
    reminders_enabled: boolean | null;
  } | null;
  history: Array<{
    booking_id: number;
    name: string | null;
    move_in_date: string | null;
    move_out_date: string | null;
  }>;
};

/** Read-only detail for one room. Null when the caller doesn't own it. */
export function useOwnerRoomDetail(roomId: number | null) {
  return useQuery({
    queryKey: ["owner-room-detail", roomId],
    enabled: !!roomId,
    queryFn: async (): Promise<OwnerRoomDetail | null> => {
      const { data, error } = await supabase.rpc("owner_room_detail", {
        _room_id: roomId,
      });
      if (error) throw error;
      return (data ?? null) as OwnerRoomDetail | null;
    },
  });
}

/** One apartment, read-only — where an apartment-level upload happens. */
export type OwnerApartmentDetail = {
  apartment: {
    id: number;
    label: string | null;
    floor: string | null;
    size_sqm: number | null;
    num_rooms: number | null;
    property_id: number;
    property_title: string | null;
  } | null;
  rooms: Array<{
    id: number;
    title: string | null;
    floor: string | null;
    size_sqm: number | null;
    monthly_rent: number | null;
    occupied: boolean;
  }>;
};

export function useOwnerApartmentDetail(apartmentId: number | null) {
  return useQuery({
    queryKey: ["owner-apartment-detail", apartmentId],
    enabled: !!apartmentId,
    queryFn: async (): Promise<OwnerApartmentDetail | null> => {
      const { data, error } = await supabase.rpc("owner_apartment_detail", {
        _apartment_id: apartmentId,
      });
      if (error) throw error;
      return (data ?? null) as OwnerApartmentDetail | null;
    },
  });
}

export type OwnerTenantPayment = {
  id: string;
  type: "rent" | "deposit" | "membership";
  amount_cents: number;
  status: "pending" | "scheduled" | "paid" | "failed" | "canceled" | "overdue";
  due_date: string | null;
  paid_at: string | null;
  /**
   * Inputs to isPaymentEffectivelyPaid — `status: 'paid'` alone does not clear
   * the deposit gate for a manual row. Reduced on purpose: a boolean instead of
   * the Stripe ids, and no marked_paid_by (see the RPC, 20260813171000).
   */
  is_stripe: boolean;
  marked_paid_at: string | null;
  proof_status: string | null;
  /** Carries metadata.referral_free_month, read by isReferralFreeMonth(). */
  metadata: unknown;
};

/**
 * Adapt an owner payment row to the shape isPaymentEffectivelyPaid expects, so
 * the owner dialog derives "deposit paid" through the same predicate as the
 * admin profile instead of a second, drifting copy of the rule.
 */
export function ownerPaymentAsPredicateInput(p: OwnerTenantPayment) {
  return {
    id: p.id,
    status: p.status,
    // The predicate only branches on presence, never on the value.
    stripe_payment_intent_id: p.is_stripe ? "stripe" : null,
    stripe_charge_id: null,
    marked_paid_at: p.marked_paid_at,
    marked_paid_by: null,
  };
}

/** Read-only payment history for one booking — for the owner-facing tenant detail view. */
export function useOwnerTenantPayments(bookingId: number | null) {
  return useQuery({
    queryKey: ["owner-tenant-payments", bookingId],
    enabled: !!bookingId,
    queryFn: async (): Promise<OwnerTenantPayment[]> => {
      const { data, error } = await supabase.rpc("owner_tenant_payments", {
        _booking_id: bookingId,
      });
      if (error) throw error;
      return (data ?? []) as OwnerTenantPayment[];
    },
  });
}

/**
 * The full tenant record behind the owner tenant dialog — everything
 * owner_property_detail's summary row leaves out.
 *
 * The journey-gate fields are named exactly as the `bookings` columns are,
 * because this object is passed straight to `resolveTenantJourney` in place of
 * a booking row. Keep them in sync with the RPC (20260813171000).
 */
export type OwnerTenantFull = {
  booking_id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;

  /** Target ids for the dialog's document uploads (20260818120000). Optional
   *  so the app degrades to the old property-wide list if the migration has
   *  not been applied yet, rather than filing against `undefined`. */
  room_id?: number | null;
  apartment_id?: number | null;
  room_title: string | null;
  apartment_label: string | null;
  property_title: string | null;
  property_address: string | null;
  property_zip_code: string | null;
  property_city: string | null;

  move_in_date: string | null;
  move_out_date: string | null;
  start_date: string | null;
  end_date: string | null;
  monthly_rent: number | null;
  deposit_amount: number | null;
  payment_status: string | null;
  tenant_active: boolean | null;

  // Journey-gate inputs — column-named on purpose (see above).
  status: string | null;
  contract_status: string | null;
  contract_signed_at: string | null;
  contract_url: string | null;
  contract_pdf_url: string | null;
  signed_contract_url: string | null;
  manual_contract_upload: boolean | null;
  docusign_envelope_id: string | null;
  docusign_status: string | null;
  key_picked_up_at: string | null;
  key_returned_at: string | null;
  move_in_confirmed_at: string | null;
  move_out_notice_at: string | null;
  actual_move_in_date: string | null;

  move_in_protocol_submitted: boolean;
  move_out_protocol_submitted: boolean;
};

/**
 * Full read-only tenant detail for one booking. Returns null when the caller
 * does not own the booking's property — the RPC does not distinguish that from
 * a booking that no longer exists.
 */
export function useOwnerTenantDetail(bookingId: number | null) {
  return useQuery({
    queryKey: ["owner-tenant-detail", bookingId],
    enabled: !!bookingId,
    queryFn: async (): Promise<OwnerTenantFull | null> => {
      const { data, error } = await supabase.rpc("owner_tenant_detail", {
        _booking_id: bookingId,
      });
      if (error) throw error;
      return (data ?? null) as OwnerTenantFull | null;
    },
  });
}
